import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { CelebrationOverlay } from "@/components/celebration/CelebrationOverlay";
import { playWinFeedback, stopConfettiSound } from "@/lib/alerts/alertFeedback";

export type CelebrationKind = "purchase" | "auction_won" | "generic";

export type CelebrateOptions = {
  kind?: CelebrationKind;
  title?: string;
  /** Listing / vehicle name shown under the headline */
  listingTitle?: string;
  subtitle?: string;
  /** Longer explanation (reservation / next steps) */
  detail?: string;
  withFeedback?: boolean;
};

type CelebrationState = {
  open: boolean;
  kind: CelebrationKind;
  title?: string;
  listingTitle?: string;
  subtitle?: string;
  detail?: string;
};

type CelebrationContextValue = {
  celebrate: (opts?: CelebrateOptions) => void;
  dismiss: () => void;
};

const CelebrationContext = createContext<CelebrationContextValue | null>(null);

const DEFAULT: CelebrationState = {
  open: false,
  kind: "generic",
};

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CelebrationState>(DEFAULT);

  const celebrate = useCallback((opts?: CelebrateOptions) => {
    if (opts?.withFeedback !== false) {
      playWinFeedback();
    }
    setState({
      open: true,
      kind: opts?.kind ?? "generic",
      title: opts?.title,
      listingTitle: opts?.listingTitle,
      subtitle: opts?.subtitle,
      detail: opts?.detail,
    });
  }, []);

  const dismiss = useCallback(() => {
    stopConfettiSound();
    setState((s) => ({ ...s, open: false }));
  }, []);

  const value = useMemo(
    () => ({ celebrate, dismiss }),
    [celebrate, dismiss],
  );

  return (
    <CelebrationContext.Provider value={value}>
      {children}
      <CelebrationOverlay
        open={state.open}
        kind={state.kind}
        title={state.title}
        listingTitle={state.listingTitle}
        subtitle={state.subtitle}
        detail={state.detail}
        onDismiss={dismiss}
      />
    </CelebrationContext.Provider>
  );
}

export function useCelebration(): CelebrationContextValue {
  const ctx = useContext(CelebrationContext);
  if (!ctx) {
    return {
      celebrate: () => undefined,
      dismiss: () => undefined,
    };
  }
  return ctx;
}
